import Image from 'components/Common/Image'
import FareFamilyClassLabel from 'components/Flights/V2/FareFamily/FareFamilyClassLabel'
import FareFamilyFlightTileV2Baggage from 'components/Flights/V2/FareFamily/FareFamilyFlightTileV2Baggage'
import FareFamilyFlightTileV2TimeLine from 'components/Flights/V2/FareFamily/FareFamilyFlightTileV2TimeLine'
import Group from 'components/utils/Group'
import { DMY_CASUAL_FORMAT, SHORT_TIME_FORMAT_24_HOUR } from 'constants/dateFormats'
import moment from 'moment'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

const Container = styled(Group)`
  padding: ${rem(16)} ${rem(20)};
`

const PrimaryContent = styled.div`
  flex-grow: 1;
`

const AirlineLogoContainer = styled.div`
  width: ${rem(120)};
  height: ${rem(52)};
`

const AirlineLogoImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const BaggageGroup = styled(Group)`
  min-width: ${rem(220)};
`

interface Props {
  flight: App.Checkout.FlightItemFlight
}

function FlightCartReviewTileExtension({ flight }: Props) {
  const baggageInfo = useMemo(() => ({
    carryOnIncluded: flight.hasIncludedCarryOnBaggage ?? flight.fareFamily?.fareFamily?.fareFeatures.some(
      feature => feature.type === 'carry_on_bag' && feature.status === 'INCLUDED',
    ),
    checkedBaggageIncluded: flight.hasIncludedCheckedBaggage ?? flight.fareFamily?.fareFamily?.fareFeatures.some(
      feature => feature.type === 'checked_baggage' && feature.status === 'INCLUDED',
    ),
  }), [flight.fareFamily?.fareFamily?.fareFeatures, flight.hasIncludedCarryOnBaggage, flight.hasIncludedCheckedBaggage])

  const formattedTimes = useMemo(() => ({
    departureTime: moment(flight.departureDateTime).format(SHORT_TIME_FORMAT_24_HOUR),
    arrivalTime: moment(flight.arrivalDateTime).format(SHORT_TIME_FORMAT_24_HOUR),
    departureDate: moment(flight.departureDateTime).format(DMY_CASUAL_FORMAT),
    arrivalDate: moment(flight.arrivalDateTime).format(DMY_CASUAL_FORMAT),
    duration: flight.duration ? `${flight.duration?.hours}h ${flight.duration?.minutes}m` : undefined,
  }), [flight.departureDateTime, flight.arrivalDateTime, flight.duration])

  return (
    <Container direction="vertical" tabletDirection="horizontal" gap={16} desktopGap={32} tabletWrap="wrap">
      <PrimaryContent>
        <Group direction="horizontal" gap={16} desktopGap={48} verticalAlign="center">
          <AirlineLogoContainer>
            <AirlineLogoImage
              width={40}
              image={flight.carrierImage}
            />
          </AirlineLogoContainer>

          <FareFamilyFlightTileV2TimeLine
            departureTime={formattedTimes.departureTime}
            arrivalTime={formattedTimes.arrivalTime}
            arrivalDate={formattedTimes.arrivalDate}
            departureDate={formattedTimes.departureDate}
            departureAirport={flight.departingAirportCode}
            arrivalAiport={flight.arrivalAirportCode}
            duration={formattedTimes.duration}
            stops={flight.numberOfStops || 0}
          />
        </Group>
      </PrimaryContent>

      <BaggageGroup direction="horizontal-reverse" desktopDirection="horizontal" gap={8} desktopGap={32} horizontalAlign="start" verticalAlign="center">
        <Group direction="horizontal" desktopDirection="vertical" gap={8} verticalAlign="center">
          <FareFamilyFlightTileV2Baggage
            carryOnIncluded={baggageInfo.carryOnIncluded}
            checkedBaggageIncluded={baggageInfo.checkedBaggageIncluded}
          />
        </Group>
        {flight.fareFamily && (
          <FareFamilyClassLabel fareFamily={flight.fareFamily} />
        )}
      </BaggageGroup>
    </Container>
  )
}

export default React.memo(FlightCartReviewTileExtension)
